import { SESSION_TIMEOUT } from '@common/constants';
import env from '@config/env';
import { ROUTE_PATHS } from '@config/routes';
import { logout, logoutUser } from '@redux/auth/authSlice';
import { clearPermissions } from '@redux/permission/permissionSlice';
import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

export const usePerformLogout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  return useCallback(async () => {
    await dispatch(logoutUser());
    dispatch(logout());
    dispatch(clearPermissions());
    navigate(ROUTE_PATHS.LOGIN);
  }, [dispatch, navigate]);
};

const useAuth = () => {
  const { user, isAuthenticated, token, loading, error } = useSelector((state) => state.auth);
  const timeoutRef = useRef(null);
  const handleLogout = usePerformLogout();

  const resetSessionTimeout = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    // Admin sessions end only when the refresh-token API returns 401 (see interceptor).
    if (isAuthenticated && env.APP_TYPE !== 'admin') {
      timeoutRef.current = setTimeout(() => {
        handleLogout();
      }, SESSION_TIMEOUT);
    }
  }, [isAuthenticated, handleLogout]);

  useEffect(() => {
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach((event) => window.addEventListener(event, resetSessionTimeout));
    resetSessionTimeout();

    return () => {
      events.forEach((event) => window.removeEventListener(event, resetSessionTimeout));
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [resetSessionTimeout]);

  return {
    user,
    isAuthenticated,
    token,
    loading,
    error,
    logout: handleLogout,
  };
};

export default useAuth;
